import type { GameState } from '@/gameplay/GameState';
import {
  missionAvailable,
  missionCompletedFlag,
  type MissionDefinition,
} from '@/gameplay/Mission';

export class MissionRegistry {
  readonly #definitions = new Map<string, MissionDefinition>();

  register(definition: MissionDefinition): void {
    if (this.#definitions.has(definition.id)) {
      throw new Error(`Mission "${definition.id}" is already registered`);
    }
    this.#definitions.set(definition.id, definition);
  }

  get(id: string): MissionDefinition {
    const definition = this.#definitions.get(id);
    if (!definition) throw new Error(`Unknown mission "${id}"`);
    return definition;
  }

  maybeGet(id: string): MissionDefinition | undefined {
    return this.#definitions.get(id);
  }

  all(): readonly MissionDefinition[] {
    return [...this.#definitions.values()];
  }

  available(state: Pick<GameState, 'flag'>): readonly MissionDefinition[] {
    return this.all().filter((definition) => (
      !state.flag(missionCompletedFlag(definition.id)) && missionAvailable(definition, state)
    ));
  }
}

export function createMissionRegistry(definitions: readonly MissionDefinition[]): MissionRegistry {
  const registry = new MissionRegistry();
  for (const definition of definitions) registry.register(definition);
  return registry;
}
